import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { logout } from "@/lib/api";
import { getMyCompany } from "@/lib/companyApi";
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Sparkles,
  ClipboardList,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  MessageSquare,
  Plug,
  CalendarClock,
  BarChart3,
  ClipboardCheck,
  CreditCard,
  GraduationCap,
} from "lucide-react";
import WishireLogo from "@/components/wishire/WishireLogo";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/company", end: true, label: "Dashboard", icon: LayoutDashboard, testId: "co-nav-dashboard" },
      { to: "/company/analytics", label: "Analytics", icon: BarChart3, testId: "co-nav-analytics" },
    ],
  },
  {
    label: "Hiring",
    items: [
      { to: "/company/jobs", label: "Jobs", icon: Briefcase, testId: "co-nav-jobs" },
      { to: "/company/talent", label: "Talent Pool", icon: Sparkles, testId: "co-nav-talent" },
      { to: "/company/campus", label: "Campus Hiring", icon: GraduationCap, testId: "co-nav-campus" },
      { to: "/company/assessments", label: "Assessments", icon: ClipboardList, testId: "co-nav-assessments" },
      { to: "/company/scorecards", label: "Scorecards", icon: ClipboardCheck, testId: "co-nav-scorecards" },
    ],
  },
  {
    label: "Engage",
    items: [
      { to: "/company/messages", label: "Messages", icon: MessageSquare, testId: "co-nav-messages" },
      { to: "/company/schedule", label: "Schedule", icon: CalendarClock, testId: "co-nav-schedule" },
    ],
  },
  {
    label: "Workspace",
    items: [
      { to: "/company/billing", label: "Plans & Billing", icon: CreditCard, testId: "co-nav-billing" },
      { to: "/company/connectors", label: "Connectors", icon: Plug, testId: "co-nav-connectors" },
      { to: "/company/settings", label: "Settings", icon: Settings, testId: "co-nav-settings" },
    ],
  },
];

export default function CompanyLayout() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data, status } = await getMyCompany();
        if (status === 404) {
          navigate("/company/onboarding", { replace: true });
          return;
        }
        setCompany(data);
      } catch (e) {
        // network or other unexpected error; fall through to onboarding
        navigate("/company/onboarding", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {}
    setUser(null);
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#FDFBF7] text-stone-900">
      <aside
        className={`fixed md:sticky inset-y-0 left-0 z-40 w-72 bg-stone-950 text-stone-100 flex flex-col transform transition-transform md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        } md:h-screen md:top-0 border-r border-white/5`}
      >
        {/* Fixed top: logo + company card */}
        <div className="flex-shrink-0 px-5 pt-6">
          <button
            onClick={() => navigate("/")}
            className="flex items-baseline gap-1 mb-5 px-1"
            data-testid="co-logo"
          >
            <WishireLogo dark size="md" />
          </button>

          {company && (
            <div className="rounded-2xl border border-white/10 p-3.5 bg-gradient-to-br from-white/[0.07] to-white/[0.02]">
              <div className="flex items-center gap-3">
                {company.logo ? (
                  <img src={company.logo} alt="" className="w-10 h-10 rounded-xl object-cover ring-1 ring-white/10" />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-300 to-amber-400 text-stone-900 flex items-center justify-center shadow-lg shadow-amber-500/20">
                    <Building2 size={18} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-stone-50 truncate" data-testid="co-company-name">
                    {company.name}
                  </p>
                  <span
                    className={`inline-flex items-center gap-1 mt-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                      company.verified
                        ? "bg-emerald-400/15 text-emerald-300"
                        : "bg-amber-400/15 text-amber-300"
                    }`}
                  >
                    <span className={`w-1 h-1 rounded-full ${company.verified ? "bg-emerald-400" : "bg-amber-400"}`} />
                    {company.verified ? "Verified" : "Pending review"}
                  </span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-2">
                <div className="text-center">
                  <p className="font-display text-lg leading-none text-stone-50">{company.stats?.jobs_open ?? 0}</p>
                  <p className="text-[10px] uppercase tracking-wider text-stone-400 mt-1">Open jobs</p>
                </div>
                <div className="text-center border-l border-white/10">
                  <p className="font-display text-lg leading-none text-stone-50">{company.stats?.applications ?? 0}</p>
                  <p className="text-[10px] uppercase tracking-wider text-stone-400 mt-1">Applicants</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 min-h-0 co-scroll">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mb-5">
              <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-500">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={n.testId}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 ${
                        isActive
                          ? "bg-white/10 text-white"
                          : "text-stone-400 hover:bg-white/5 hover:text-stone-100"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-amber-300" />
                        )}
                        <n.icon
                          size={18}
                          className={isActive ? "text-amber-300" : "text-stone-500 group-hover:text-stone-300"}
                        />
                        <span className="text-sm font-medium">{n.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Fixed bottom: user + logout */}
        <div className="flex-shrink-0 px-3 pb-4 pt-3 border-t border-white/10 bg-stone-950">
          <div className="flex items-center gap-3 px-2 py-2 rounded-xl">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-9 h-9 rounded-full object-cover ring-1 ring-white/10"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 to-amber-400 text-stone-900 font-display flex items-center justify-center shrink-0">
                {(user?.name || "?")[0]}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-stone-100 truncate">{user?.name}</p>
              <p className="text-xs text-stone-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            type="button"
            data-testid="co-logout"
            onClick={handleLogout}
            className="mt-1.5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-stone-400 hover:bg-red-500/10 hover:text-red-300 transition-colors text-sm font-medium"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 safe-top safe-x z-30 bg-[#FDFBF7]/90 backdrop-blur border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <WishireLogo size="sm" wordOnly />
        <button
          aria-label="Open menu"
          data-testid="co-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="p-2 rounded-full border border-stone-300"
        >
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-30"
          onClick={() => setOpen(false)}
        />
      )}

      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        {loading ? (
          <div className="min-h-screen flex items-center justify-center text-stone-500">
            Loading workspace…
          </div>
        ) : (
          <Outlet context={{ company, setCompany }} />
        )}
      </main>
    </div>
  );
}
